import { useEffect, useRef, useState } from "react";
import api, { fmtMXN, fmtMXNFull, fmtDate, todayLocal } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader, { StatusBadge } from "@/components/PageHeader";
import { Modal, In, Sel } from "./Projects";
import ClientPicker from "@/components/ClientPicker";
import { Plus, ChevronRight, ChevronDown, Receipt, RefreshCcw, Trash2, X, Download, Loader2, Wallet, TrendingUp, CalendarClock, Users } from "lucide-react";
import { toast } from "sonner";

const PAYMENT_METHODS = [
  { value: "efectivo", label: "Efectivo" },
  { value: "transferencia", label: "Transferencia" },
  { value: "deposito", label: "Depósito" },
  { value: "cheque", label: "Cheque" },
  { value: "otro", label: "Otro" },
];

const STATUS = [
  { value: "active", label: "Activo", color: "var(--cyan)" },
  { value: "closed", label: "Liquidado", color: "var(--green)" },
];

const emptyLoan = {
  borrower_name: "",
  borrower_rfc: "",
  borrower_phone: "",
  borrower_email: "",
  contracting_entity_id: "",
  principal: 0,
  monthly_rate_pct: 0,
  start_date: todayLocal(),
  interest_day: 1,
  payment_method: "transferencia",
  reference: "",
  notes: "",
};

export default function Loans() {
  const { user } = useAuth();
  const [loans, setLoans] = useState([]);
  const [summary, setSummary] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState(emptyLoan);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState({}); // groupKey -> bool

  const canWrite = ["admin", "contador"].includes(user?.role);

  const load = async () => {
    const params = statusFilter ? { status_filter: statusFilter } : {};
    const [{ data: list }, { data: sum }] = await Promise.all([
      api.get("/loans", { params }),
      api.get("/loans/summary"),
    ]);
    setLoans(list || []);
    setSummary(sum);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/loans/run-monthly-accruals");
      if (data.accruals_created > 0) {
        toast.success(`${data.accruals_created} intereses mensuales generados`);
      } else {
        toast.success("Sin intereses pendientes por generar");
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar intereses");
    } finally {
      setRefreshing(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const clientOpId = (window.crypto?.randomUUID && window.crypto.randomUUID()) || `loan-${Date.now()}`;
    try {
      await api.post("/loans", {
        ...form,
        principal: Number(form.principal),
        monthly_rate_pct: Number(form.monthly_rate_pct),
        interest_day: Number(form.interest_day),
        client_op_id: clientOpId,
      });
      toast.success("Préstamo creado");
      setOpenCreate(false);
      setForm(emptyLoan);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este préstamo y TODOS sus movimientos? Esta acción es irreversible.")) return;
    try {
      await api.delete(`/loans/${id}`);
      toast.success("Préstamo eliminado");
      if (selected?.id === id) setSelected(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  // Monthly interest preview for the form
  const previewMonthly = (Number(form.principal || 0) * Number(form.monthly_rate_pct || 0)) / 100;

  // Agrupar préstamos por cliente (entidad vinculada o nombre normalizado)
  const groups = [];
  {
    const map = {};
    for (const ln of loans) {
      const key = ln.contracting_entity_id || (ln.borrower_name || "").trim().toUpperCase();
      if (!map[key]) {
        map[key] = { key, name: ln.borrower_name, rfc: ln.borrower_rfc, loans: [] };
        groups.push(map[key]);
      }
      map[key].loans.push(ln);
    }
    for (const g of groups) {
      const act = g.loans.filter((l) => l.status === "active");
      g.activeCount = act.length;
      g.principal = g.loans.reduce((s, l) => s + (l.principal || 0), 0);
      g.monthlyInterest = act.reduce((s, l) => s + (l.balance?.monthly_interest_amount || 0), 0);
      g.capOut = g.loans.reduce((s, l) => s + (l.balance?.capital_outstanding || 0), 0);
      g.intOut = g.loans.reduce((s, l) => s + (l.balance?.interest_outstanding || 0), 0);
      g.totalOut = g.loans.reduce((s, l) => s + (l.balance?.total_outstanding || 0), 0);
      g.nextDate = act.map((l) => l.balance?.next_accrual_date).filter(Boolean).sort()[0] || null;
      const rates = [...new Set(g.loans.map((l) => l.monthly_rate_pct))];
      g.rateLabel = rates.length === 1 ? `${rates[0].toFixed(2)}%` : `${Math.min(...rates).toFixed(2)}–${Math.max(...rates).toFixed(2)}%`;
    }
  }

  const downloadStatement = async (g) => {
    try {
      const params = g.entityId ? { entity_id: g.entityId } : { name: g.name };
      const res = await api.get("/loans/client-statement/pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `estado_cuenta_${(g.name || "cliente").replace(/[^a-zA-Z0-9 _-]/g, "").trim().replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Estado de cuenta descargado — listo para enviar al cliente");
    } catch {
      toast.error("No se pudo generar el estado de cuenta");
    }
  };

  const loanRow = (ln, child = false) => {
    const b = ln.balance || {};
    return (
      <tr key={ln.id} data-testid={`loan-row-${ln.id}`} className="row-hover"
        style={{ borderBottom: "1px solid var(--border)", background: child ? "rgba(34,211,238,0.03)" : undefined }}>
        <Td>
          <div className={child ? "pl-6" : ""} style={child ? { borderLeft: "2px solid rgba(34,211,238,0.35)", marginLeft: 4 } : undefined}>
            <div className={`font-semibold ${child ? "text-slate-300 text-xs" : "text-slate-100"}`}>{child ? `Préstamo · ${fmtDate(ln.start_date)}` : ln.borrower_name}</div>
            {!child && ln.borrower_rfc && <div className="text-[10px] text-slate-500 font-mono">{ln.borrower_rfc}</div>}
            <div className="text-[10px] text-slate-500">{child ? `Corte día ${ln.interest_day}${ln.reference ? ` · ref: ${ln.reference}` : ""}` : `Inicio: ${fmtDate(ln.start_date)} · Corte día ${ln.interest_day}`}</div>
          </div>
        </Td>
        <Td right><span className="font-mono font-semibold text-slate-100">{fmtMXN(ln.principal)}</span></Td>
        <Td right><span className="font-mono tabular-nums" style={{ color: "var(--magenta)" }}>{ln.monthly_rate_pct.toFixed(2)}%</span></Td>
        <Td right><span className="font-mono tabular-nums text-slate-300">{fmtMXN(b.monthly_interest_amount || 0)}</span></Td>
        <Td right>
          <div className="font-mono font-bold tabular-nums" style={{ color: b.total_outstanding > 0 ? "#f59e0b" : "#16a34a" }}>
            {fmtMXN(b.total_outstanding || 0)}
          </div>
          <div className="text-[10px] text-slate-500 font-mono">
            Cap: {fmtMXN(b.capital_outstanding || 0)} · Int: {fmtMXN(b.interest_outstanding || 0)}
          </div>
        </Td>
        <Td><span className="font-mono text-xs text-slate-300">{ln.status === "active" ? fmtDate(b.next_accrual_date) : "—"}</span></Td>
        <Td><StatusBadge items={STATUS} value={ln.status} /></Td>
        <Td right>
          <button
            data-testid={`loan-open-${ln.id}`}
            onClick={() => setSelected({ ...ln })}
            className="text-xs uppercase tracking-widest font-semibold hover:underline mr-2"
            style={{ color: "var(--cyan)" }}
          >
            Detalle <ChevronRight className="w-3 h-3 inline" />
          </button>
          {!child && (
            <button
              data-testid={`loan-statement-${ln.id}`}
              onClick={() => downloadStatement({ entityId: ln.contracting_entity_id, name: ln.borrower_name })}
              className="p-1 rounded-sm hover:bg-white/5 mr-1"
              style={{ color: "var(--magenta)" }}
              title="Estado de cuenta PDF del cliente"
            >
              <Download className="w-4 h-4" />
            </button>
          )}
          {user?.role === "admin" && (
            <button
              data-testid={`loan-delete-${ln.id}`}
              onClick={() => remove(ln.id)}
              className="p-1 rounded-sm hover:bg-white/5"
              style={{ color: "var(--red, #dc2626)" }}
              title="Eliminar préstamo"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </Td>
      </tr>
    );
  };

  return (
    <div data-testid="loans-page">
      <PageHeader
        subtitle="Préstamos otorgados a clientes y terceros"
        title="Préstamos"
        actions={
          <div className="flex gap-2 flex-wrap">
            <button
              data-testid="loans-refresh-btn"
              onClick={refresh}
              disabled={refreshing}
              className="btn-ghost px-3 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-2"
              title="Genera intereses pendientes para todos los préstamos activos"
            >
              {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
              Generar intereses
            </button>
            {canWrite && (
              <button
                data-testid="new-loan-btn"
                onClick={() => { setForm(emptyLoan); setOpenCreate(true); }}
                className="btn-primary px-3 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> Nuevo préstamo
              </button>
            )}
          </div>
        }
      />

      <div className="px-6 lg:px-8 py-6 space-y-5">
        {/* Summary KPIs */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi icon={Wallet} label="Capital pendiente" value={fmtMXNFull(summary.total_principal_outstanding)} color="var(--cyan)" testId="loans-kpi-capital" />
            <Kpi icon={TrendingUp} label="Interés pendiente" value={fmtMXNFull(summary.total_interest_pending)} color="var(--magenta)" testId="loans-kpi-interest" />
            <Kpi icon={CalendarClock} label="Total por cobrar" value={fmtMXNFull(summary.total_outstanding)} color="#f59e0b" testId="loans-kpi-total" />
            <Kpi icon={Receipt} label="Préstamos activos" value={`${summary.active_count} / ${summary.active_count + summary.closed_count}`} color="#16a34a" testId="loans-kpi-active" raw />
          </div>
        )}

        {/* Filters */}
        <div className="panel p-3 flex items-center justify-between flex-wrap gap-2">
          <span className="label-mono" style={{ color: "var(--cyan)" }}>// LISTADO</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input-cyber px-3 py-1.5 text-sm"
            data-testid="loans-status-filter"
          >
            <option value="" style={{ background: "#0a0f1d" }}>Todos los estados</option>
            <option value="active" style={{ background: "#0a0f1d" }}>Activos</option>
            <option value="closed" style={{ background: "#0a0f1d" }}>Liquidados</option>
          </select>
        </div>

        {/* List */}
        <div className="panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]" data-testid="loans-table">
              <thead className="panel-header">
                <tr className="text-left">
                  <Th>Cliente</Th>
                  <Th right>Capital inicial</Th>
                  <Th right>Tasa mensual</Th>
                  <Th right>Interés/mes</Th>
                  <Th right>Saldo total</Th>
                  <Th>Próximo corte</Th>
                  <Th>Estado</Th>
                  <Th right>Acciones</Th>
                </tr>
              </thead>
              <tbody>
                {loans.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-12 text-slate-500 text-sm">
                    Sin préstamos registrados. {canWrite && "Click en 'NUEVO PRÉSTAMO' para empezar."}
                  </td></tr>
                )}
                {groups.map((g) => {
                  if (g.loans.length === 1) return loanRow(g.loans[0]);
                  const open = !!expanded[g.key];
                  return [
                    <tr key={g.key} data-testid={`loan-group-${g.key}`} onClick={() => setExpanded((e) => ({ ...e, [g.key]: !open }))}
                      className="row-hover cursor-pointer" style={{ borderBottom: "1px solid var(--border)", background: "rgba(217,70,239,0.04)" }}>
                      <Td>
                        <div className="flex items-center gap-2">
                          {open ? <ChevronDown className="w-4 h-4 shrink-0" style={{ color: "var(--magenta)" }} /> : <ChevronRight className="w-4 h-4 shrink-0" style={{ color: "var(--magenta)" }} />}
                          <div>
                            <div className="font-semibold text-slate-100 flex items-center gap-2">
                              <Users className="w-3.5 h-3.5" style={{ color: "var(--magenta)" }} /> {g.name}
                            </div>
                            {g.rfc && <div className="text-[10px] text-slate-500 font-mono">{g.rfc}</div>}
                            <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-widest" style={{ background: "rgba(217,70,239,0.12)", color: "var(--magenta)" }} data-testid={`loan-group-count-${g.key}`}>
                              {g.loans.length} préstamos consolidados
                            </span>
                          </div>
                        </div>
                      </Td>
                      <Td right><span className="font-mono font-semibold text-slate-100">{fmtMXN(g.principal)}</span></Td>
                      <Td right><span className="font-mono tabular-nums" style={{ color: "var(--magenta)" }}>{g.rateLabel}</span></Td>
                      <Td right><span className="font-mono tabular-nums text-slate-300">{fmtMXN(g.monthlyInterest)}</span></Td>
                      <Td right>
                        <div className="font-mono font-bold tabular-nums" style={{ color: g.totalOut > 0 ? "#f59e0b" : "#16a34a" }} data-testid={`loan-group-total-${g.key}`}>{fmtMXN(g.totalOut)}</div>
                        <div className="text-[10px] text-slate-500 font-mono">Cap: {fmtMXN(g.capOut)} · Int: {fmtMXN(g.intOut)}</div>
                      </Td>
                      <Td><span className="font-mono text-xs text-slate-300">{g.nextDate ? fmtDate(g.nextDate) : "—"}</span></Td>
                      <Td>
                        <span className="px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase" style={{ background: g.activeCount > 0 ? "rgba(34,211,238,0.12)" : "rgba(34,197,94,0.12)", color: g.activeCount > 0 ? "var(--cyan)" : "var(--green, #16a34a)" }}>
                          {g.activeCount > 0 ? `${g.activeCount}/${g.loans.length} activos` : "Liquidados"}
                        </span>
                      </Td>
                      <Td right>
                        <button
                          data-testid={`loan-group-statement-${g.key}`}
                          onClick={(e) => { e.stopPropagation(); downloadStatement({ entityId: g.loans[0].contracting_entity_id, name: g.name }); }}
                          className="p-1 rounded-sm hover:bg-white/5 mr-2 align-middle"
                          style={{ color: "var(--magenta)" }}
                          title="Estado de cuenta PDF consolidado"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--magenta)" }}>
                          {open ? "Ocultar" : "Ver"} {g.loans.length}
                        </span>
                      </Td>
                    </tr>,
                    ...(open ? g.loans.map((ln) => loanRow(ln, true)) : []),
                  ];
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create modal */}
      {openCreate && (
        <Modal
          onClose={() => setOpenCreate(false)}
          title="Nuevo préstamo"
          footer={
            <>
              <button type="button" onClick={() => setOpenCreate(false)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
              <button type="submit" form="loan-form" data-testid="save-loan-btn" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Guardar</button>
            </>
          }
        >
          <form id="loan-form" onSubmit={submit} className="space-y-4">
            <div className="px-3 py-2 rounded-sm text-xs" style={{ background: "rgba(34,211,238,0.08)", border: "1px solid rgba(34,211,238,0.35)", color: "#67e8f9" }}>
              Registra un préstamo otorgado a un cliente. El interés se genera automáticamente cada mes según la tasa y el día de corte.
            </div>

            <div>
              <label className="label-mono block mb-1.5">Cliente del catálogo (opcional)</label>
              <ClientPicker
                value={form.contracting_entity_id || null}
                onChange={async (id) => {
                  if (!id) {
                    setForm({ ...form, contracting_entity_id: "" });
                    return;
                  }
                  try {
                    const { data } = await api.get(`/contracting-entities/${id}`);
                    // Auto-poblar campos del deudor con datos del cliente
                    setForm({
                      ...form,
                      contracting_entity_id: id,
                      borrower_name: data.name || form.borrower_name,
                      borrower_rfc: data.rfc || form.borrower_rfc,
                      borrower_phone: data.phone || form.borrower_phone,
                      borrower_email: data.email || form.borrower_email,
                    });
                  } catch {
                    setForm({ ...form, contracting_entity_id: id });
                  }
                }}
                label=""
                placeholder="Seleccionar cliente existente…"
                allowEmpty
              />
              <p className="text-[11px] text-slate-500 mt-1">Si vinculas un cliente, se llenan automáticamente nombre, RFC, teléfono y correo.</p>
            </div>

            <In label="Nombre del deudor / cliente" testId="loan-borrower-name" value={form.borrower_name} onChange={(v) => setForm({ ...form, borrower_name: v })} />
            <div className="grid grid-cols-3 gap-3">
              <In label="RFC (opcional)" testId="loan-rfc" value={form.borrower_rfc} onChange={(v) => setForm({ ...form, borrower_rfc: v })} required={false} />
              <In label="Teléfono (opcional)" testId="loan-phone" value={form.borrower_phone} onChange={(v) => setForm({ ...form, borrower_phone: v })} required={false} />
              <In label="Correo (opcional)" testId="loan-email" type="email" value={form.borrower_email} onChange={(v) => setForm({ ...form, borrower_email: v })} required={false} />
            </div>

            <div className="panel p-3 space-y-3" style={{ background: "rgba(8,12,24,0.6)" }}>
              <div className="label-mono" style={{ color: "var(--magenta)" }}>// CONDICIONES DEL PRÉSTAMO</div>
              <div className="grid grid-cols-2 gap-3">
                <In label="Capital prestado (MXN)" testId="loan-principal" type="number" value={form.principal} onChange={(v) => setForm({ ...form, principal: v })} />
                <In label="Tasa mensual %" testId="loan-rate" type="number" value={form.monthly_rate_pct} onChange={(v) => setForm({ ...form, monthly_rate_pct: v })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <In label="Fecha de inicio" testId="loan-start-date" type="date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} />
                <In label="Día de corte (1-28)" testId="loan-interest-day" type="number" value={form.interest_day} onChange={(v) => setForm({ ...form, interest_day: v })} />
              </div>
              <div className="px-3 py-2 rounded-sm text-xs" style={{ background: "rgba(217,70,239,0.08)", border: "1px solid rgba(217,70,239,0.3)" }}>
                <div className="label-mono mb-1" style={{ color: "var(--magenta)" }}>// VISTA PREVIA</div>
                <div className="text-slate-300">
                  Interés <strong className="text-slate-100">mensual</strong>: <span className="font-mono font-bold text-slate-100">{fmtMXN(previewMonthly)}</span>
                  <span className="text-slate-500"> (cada día {form.interest_day || 1} del mes)</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  Interés calculado sobre el capital inicial (interés simple). Se genera automáticamente cada mes hasta liquidar el préstamo.
                </div>
              </div>
            </div>

            <Sel label="Método de pago habitual" testId="loan-payment-method" value={form.payment_method} onChange={(v) => setForm({ ...form, payment_method: v })} options={PAYMENT_METHODS} />
            <In label="Referencia (opcional)" testId="loan-reference" value={form.reference} onChange={(v) => setForm({ ...form, reference: v })} required={false} />
            <div>
              <label className="label-mono block mb-1.5">Notas (opcional)</label>
              <textarea data-testid="loan-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full input-cyber px-3 py-2 text-sm" placeholder="Ej: Préstamo personal con plazo abierto" />
            </div>
          </form>
        </Modal>
      )}

      {/* Detail slide-over */}
      {selected && <LoanDetail loan={selected} onClose={() => setSelected(null)} onChanged={load} canWrite={canWrite} />}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, color, raw, testId }) {
  return (
    <div className="panel p-3" data-testid={testId}>
      <div className="flex items-center justify-between mb-1.5">
        <Icon className="w-4 h-4" style={{ color }} />
        <div className="label-mono text-[9px] uppercase tracking-widest text-slate-400">{label}</div>
      </div>
      <div className={`font-display font-black tabular-nums ${raw ? "text-2xl" : "text-xl"}`} style={{ color }}>{value}</div>
    </div>
  );
}

function LoanDetail({ loan: initialLoan, onClose, onChanged, canWrite }) {
  const [loan, setLoan] = useState(initialLoan);
  const [refreshing, setRefreshing] = useState(false);
  const [openPay, setOpenPay] = useState(false);
  const [payForm, setPayForm] = useState({ amount: 0, movement_date: todayLocal(), payment_method: "transferencia", reference: "", description: "" });

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get(`/loans/${loan.id}`);
      setLoan(data);
    } finally { setRefreshing(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submitPayment = async (e) => {
    e.preventDefault();
    const clientOpId = (window.crypto?.randomUUID && window.crypto.randomUUID()) || `pay-${Date.now()}`;
    try {
      await api.post(`/loans/${loan.id}/payment`, {
        loan_id: loan.id,
        movement_type: "payment",
        amount: Number(payForm.amount),
        movement_date: payForm.movement_date,
        payment_method: payForm.payment_method,
        reference: payForm.reference || null,
        description: payForm.description || null,
        client_op_id: clientOpId,
      });
      toast.success("Pago registrado. Aplicado automáticamente a interés y capital.");
      setOpenPay(false);
      setPayForm({ amount: 0, movement_date: todayLocal(), payment_method: "transferencia", reference: "", description: "" });
      await load();
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar pago");
    }
  };

  const downloadReceipt = async (movId) => {
    try {
      const res = await api.get(`/loan-movements/${movId}/receipt`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch {
      toast.error("No se pudo generar el PDF");
    }
  };

  const removeMovement = async (movId) => {
    if (!window.confirm("¿Eliminar este movimiento?")) return;
    try {
      await api.delete(`/loan-movements/${movId}`);
      toast.success("Movimiento eliminado");
      await load();
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const b = loan.balance || {};
  const movs = loan.movements || [];

  return (
    <div
      data-testid="loan-detail"
      className="fixed inset-0 z-50 flex justify-end"
      style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl h-full overflow-y-auto"
        style={{ background: "#0a0f1d", borderLeft: "1px solid var(--border-2)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 px-6 py-4 flex items-center justify-between" style={{ background: "var(--bg-2)", borderBottom: "1px solid var(--border)" }}>
          <div>
            <div className="label-mono" style={{ color: "var(--cyan)" }}>// DETALLE</div>
            <h2 className="font-display font-bold text-xl text-slate-100">{loan.borrower_name}</h2>
            <div className="text-xs text-slate-500">
              <StatusBadge items={STATUS} value={loan.status} /> · {loan.monthly_rate_pct.toFixed(2)}% mensual · Día corte: {loan.interest_day}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-cyan-300" data-testid="loan-detail-close">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Balance summary */}
          <div className="grid grid-cols-2 gap-3">
            <Kpi icon={Wallet} label="Capital inicial" value={fmtMXNFull(loan.principal)} color="var(--cyan)" />
            <Kpi icon={Receipt} label="Capital pendiente" value={fmtMXNFull(b.capital_outstanding)} color={b.capital_outstanding > 0 ? "#f59e0b" : "#16a34a"} />
            <Kpi icon={TrendingUp} label="Interés acumulado" value={fmtMXNFull(b.interest_accrued)} color="var(--magenta)" />
            <Kpi icon={CalendarClock} label="Interés pendiente" value={fmtMXNFull(b.interest_outstanding)} color={b.interest_outstanding > 0 ? "#f59e0b" : "#16a34a"} />
          </div>
          <div className="panel p-3" style={{ background: "rgba(245,158,11,0.05)", border: "1px solid rgba(245,158,11,0.3)" }}>
            <div className="label-mono text-[10px]" style={{ color: "#f59e0b" }}>// TOTAL POR COBRAR</div>
            <div className="font-display font-black tabular-nums text-3xl mt-1" style={{ color: "#f59e0b" }} data-testid="loan-total-outstanding">{fmtMXNFull(b.total_outstanding)}</div>
            {loan.status === "active" && (
              <div className="text-xs text-slate-400 mt-1">
                Próximo corte de interés: <span className="font-mono font-semibold text-slate-200">{fmtDate(b.next_accrual_date)}</span>
                <span className="ml-2 text-slate-500">(período {b.next_accrual_period})</span>
              </div>
            )}
          </div>

          {canWrite && loan.status === "active" && (
            <button
              data-testid="register-payment-btn"
              onClick={() => setOpenPay(true)}
              className="w-full btn-primary py-2.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" /> Registrar pago del cliente
            </button>
          )}

          {/* Movements */}
          <div>
            <div className="label-mono mb-2" style={{ color: "var(--cyan)" }}>// MOVIMIENTOS ({movs.length})</div>
            {refreshing && <div className="text-xs text-slate-500 mb-2">Cargando…</div>}
            <div className="space-y-1.5">
              {movs.length === 0 && <div className="text-xs text-slate-500 text-center py-6">Sin movimientos aún.</div>}
              {movs.map((m) => {
                const isPayment = m.movement_type === "payment";
                const color = isPayment ? "#16a34a" : "var(--magenta)";
                return (
                  <div key={m.id} data-testid={`movement-${m.id}`} className="px-3 py-2 rounded-sm flex items-center justify-between gap-2 text-xs"
                    style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${isPayment ? "rgba(22,163,74,0.25)" : "rgba(217,70,239,0.18)"}`, borderLeft: `3px solid ${color}` }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <span className="label-mono text-[10px]" style={{ color }}>
                          {isPayment ? "PAGO RECIBIDO" : `INTERÉS · ${m.period_label || "—"}`}
                        </span>
                        {m.auto_generated && <span className="label-mono text-[9px] text-slate-500">AUTO</span>}
                      </div>
                      <div className="text-slate-100">{m.description}</div>
                      <div className="text-[10px] text-slate-500">
                        {fmtDate(m.movement_date)} {m.payment_method && `· ${m.payment_method.replace("_", " ")}`} {m.reference && `· ref: ${m.reference}`}
                      </div>
                      {isPayment && (m.interest_applied > 0 || m.principal_applied > 0) && (
                        <div className="text-[10px] mt-0.5" style={{ color: "var(--cyan)" }}>
                          → Interés: {fmtMXN(m.interest_applied)} · Capital: {fmtMXN(m.principal_applied)}
                        </div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="font-mono font-bold tabular-nums" style={{ color }}>
                        {isPayment ? "+" : ""}{fmtMXN(m.amount)}
                      </div>
                      <div className="flex items-center gap-1 justify-end mt-1">
                        <button data-testid={`movement-pdf-${m.id}`} onClick={() => downloadReceipt(m.id)} className="text-slate-500 hover:text-cyan-300 p-0.5" title="PDF">
                          <Download className="w-3.5 h-3.5" />
                        </button>
                        {canWrite && !m.auto_generated && (
                          <button onClick={() => removeMovement(m.id)} className="text-slate-500 hover:text-red-400 p-0.5" title="Eliminar">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {openPay && (
          <Modal
            onClose={() => setOpenPay(false)}
            title="Registrar pago"
            footer={
              <>
                <button type="button" onClick={() => setOpenPay(false)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
                <button type="submit" form="loan-payment-form" data-testid="save-payment-btn" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Guardar pago</button>
              </>
            }
          >
            <form id="loan-payment-form" onSubmit={submitPayment} className="space-y-3">
              <div className="px-3 py-2 rounded-sm text-xs" style={{ background: "rgba(34,211,238,0.08)", border: "1px solid rgba(34,211,238,0.3)" }}>
                El pago se aplica primero al <strong>interés acumulado pendiente</strong> ({fmtMXN(b.interest_outstanding)}), el resto reduce el capital.
              </div>
              <In label="Monto del pago (MXN)" testId="payment-amount" type="number" value={payForm.amount} onChange={(v) => setPayForm({ ...payForm, amount: v })} />
              <In label="Fecha del pago" testId="payment-date" type="date" value={payForm.movement_date} onChange={(v) => setPayForm({ ...payForm, movement_date: v })} />
              <Sel label="Método de pago" testId="payment-method" value={payForm.payment_method} onChange={(v) => setPayForm({ ...payForm, payment_method: v })} options={PAYMENT_METHODS} />
              <In label="Referencia (folio/cheque)" testId="payment-reference" value={payForm.reference} onChange={(v) => setPayForm({ ...payForm, reference: v })} required={false} />
              <div>
                <label className="label-mono block mb-1.5">Notas (opcional)</label>
                <textarea data-testid="payment-notes" rows={2} value={payForm.description} onChange={(e) => setPayForm({ ...payForm, description: e.target.value })}
                  className="w-full input-cyber px-3 py-2 text-sm" />
              </div>
            </form>
          </Modal>
        )}
      </div>
    </div>
  );
}

const Th = ({ children, right }) => (
  <th className={`label-mono px-4 py-3 ${right ? "text-right" : ""}`}>{children}</th>
);
const Td = ({ children, right, className = "" }) => (
  <td className={`px-4 py-3 align-top ${right ? "text-right" : ""} ${className}`}>{children}</td>
);
